import axios, {AxiosResponse} from 'axios'
import {ID, QueryState, Response} from '../../../../../../../../../_metronic/helpers'
import {ResponseTaskComment, TaskComment, TaskCommentsQueryResponse} from './_models'

const TASK_COMMENT_URL = `${process.env.REACT_APP_THEME_API_URL}/api/task-comments`

const createTaskComment = (taskComment: TaskComment): Promise<ResponseTaskComment | undefined> => {
  return axios
    .post(TASK_COMMENT_URL, taskComment)
    .then((response: AxiosResponse<ResponseTaskComment>) => response.data)
}

const getTaskComments = (query: string): Promise<TaskCommentsQueryResponse> => {
  return axios
    .get(`${TASK_COMMENT_URL}?${query}`)
    .then((d: AxiosResponse<TaskCommentsQueryResponse>) => d.data)
}

const updateTaskComment = (taskComment: TaskComment): Promise<ResponseTaskComment | undefined> => {
  return axios
    .put(`${TASK_COMMENT_URL}/${taskComment.id}`, taskComment)
    .then((response: AxiosResponse<ResponseTaskComment>) => response.data)
}

const deleteTaskComment = (commentId: ID): Promise<void> => {
  return axios.delete(`${TASK_COMMENT_URL}/${commentId}`).then(() => {})
}

const searchUsersForMention = (query: string): Promise<any> => {
  return axios
    .get(`${TASK_COMMENT_URL}/search-users?q=${encodeURIComponent(query)}`)
    .then((response: AxiosResponse<any>) => response.data)
}

const uploadCommentImage = (file: File, taskId: ID): Promise<any> => {
  if (!taskId) {
    return Promise.reject(new Error('task_id is required'))
  }

  const formData = new FormData()
  formData.append('image', file)
  formData.append('task_id', taskId.toString())

  return axios
    .post(`${TASK_COMMENT_URL}/upload-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((response: AxiosResponse<any>) => response.data)
}

/**
 * Convert a data URL (e.g. data:image/png;base64,...) to a File for upload.
 */
function dataURLToFile(dataUrl: string, filename: string): File {
  const [header, base64] = dataUrl.split(',')
  const mimeMatch = header.match(/:(.*?);/)
  const mime = mimeMatch ? mimeMatch[1] : 'image/png'
  const bstr = atob(base64)
  let n = bstr.length
  const u8 = new Uint8Array(n)
  while (n--) u8[n] = bstr.charCodeAt(n)
  return new File([new Blob([u8], {type: mime})], filename, {type: mime})
}

const MAX_IMAGE_SIZE_BYTES = 3 * 1024 * 1024 // 3MB

export type ProcessInlineImagesResult = { html: string; removedCount: number }

/**
 * Find all <img> with src="data:image/...;base64,..." in HTML, upload each to the server,
 * and replace src with the storage URL. Images over MAX_IMAGE_SIZE_BYTES are removed (not stored as base64).
 */
const processInlineBase64Images = async (html: string, taskId: ID): Promise<ProcessInlineImagesResult> => {
  if (!html || !html.includes('data:image') || !taskId) return { html, removedCount: 0 }

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const imgs = doc.querySelectorAll<HTMLImageElement>('img[src^="data:image"]')
  if (imgs.length === 0) return { html, removedCount: 0 }

  let removedCount = 0

  for (let i = 0; i < imgs.length; i++) {
    const img = imgs[i]
    const src = img.getAttribute('src')
    if (!src || !src.startsWith('data:image')) continue

    try {
      const ext = src.indexOf('image/png') !== -1 ? 'png' : src.indexOf('image/jpeg') !== -1 || src.indexOf('image/jpg') !== -1 ? 'jpg' : src.indexOf('image/gif') !== -1 ? 'gif' : src.indexOf('image/webp') !== -1 ? 'webp' : 'png'
      const file = dataURLToFile(src, `pasted-${Date.now()}-${i}.${ext}`)
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        img.remove()
        removedCount += 1
        continue
      }
      const response = await uploadCommentImage(file, taskId)
      const path = response.path || `tasks/${file.name}`
      const url = response.url || `${process.env.REACT_APP_THEME_API_URL}/storage/${path}`
      img.setAttribute('src', url)
      img.setAttribute('data-image-path', path)
    } catch (_) {
      // Remove img so we don't save truncated base64; user will be notified via removedCount
      img.remove()
      removedCount += 1
    }
  }

  const body = doc.body
  const outHtml = body ? body.innerHTML : html
  return { html: outHtml, removedCount }
}

export {
  createTaskComment,
  getTaskComments,
  updateTaskComment,
  deleteTaskComment,
  searchUsersForMention,
  uploadCommentImage,
  processInlineBase64Images,
}
